/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormField, TypeAdapter } from '../../../form/Form';
import CommonAdapters from '../../../form/adapters/CommonAdapters';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import './QueryBuilder.less';
import { Button, Select, Input } from 'antd';
import { CloseOutlined, FilterOutlined } from '@ant-design/icons';

type QueryBuilderItemType = { [key: string]: any };

type Props = {
  schema: FormField[];
  adapters?: TypeAdapter[];
  value?: QueryBuilderItemType;
  onChange?: (value?: QueryBuilderItemType) => void;
  search?: boolean;
  searchPlaceholder?: string;
  onSearch?: (value?: QueryBuilderItemType) => void;
};

type QueryBuilderItemProps = {
  adapters: { [key: string]: TypeAdapter };
  field: FormField;
  value?: any;
  onChange: (value?: any) => void;
  onClose: () => void;
  editing: boolean;
  setEditing: (editing: boolean) => void;
};

const QueryBuilderItem: FC<QueryBuilderItemProps> = (props) => {
  const { field, value, onChange, adapters, onClose, editing, setEditing } = props;
  if (!field.props) {
    field.props = {};
  }
  field.props.ref = useCallback((cur: any) => {
    if (cur !== null) {
      try {
        cur.focus();
      } catch (e) {
        // console.log(e);
      }
    }
  }, []);
  field.props.value = value;
  field.props.onChange = (v: any) => {
    if (v && v.target) {
      onChange(v.target.value);
    } else {
      onChange(v);
    }
  };
  field.props.onBlur = () => setEditing(false);
  return (
    <div className="qbld-item">
      <div className="qbld-item-name">{field.name}:</div>
      {editing ? (
        <div className="qbld-item-edit">{adapters[field.type as string]?.render(field)}</div>
      ) : (
        <div className="qbld-item-view" onClick={() => setEditing(true)}>
          {field.view ? field.view(value) : value}
        </div>
      )}
      <Button type="link" className="qbld-close" onClick={onClose}>
        <CloseOutlined />
      </Button>
    </div>
  );
};

export const QueryBuilder: FC<Props> = (props) => {
  const bp = useBreakpoint();
  const { schema, value } = props;
  // const [search, setSearch] = useState<string>(props.value?.['search']);
  const [visible, setVisible] = useState<boolean>(true);
  const adapters: { [key: string]: TypeAdapter } = useMemo(() => {
    const ads: { [key: string]: TypeAdapter } = {};
    CommonAdapters.all(!bp.md).forEach((value) => (ads[value.name] = value));
    if (props.adapters) {
      props.adapters.forEach((value) => (ads[value.name] = value));
    }
    return ads;
  }, [props.adapters, bp.md]);
  const [items, setItems] = useState<string[]>(Object.keys(props.value ?? {}));
  const [sel, setSel] = useState<string>();
  const [editing, setEditing] = useState<string>();
  useEffect(() => {
    if (sel) {
      setItems([...items, sel]);
      setSel(undefined);
      setEditing(sel);
    }
  }, [items, sel]);
  useEffect(() => {
    !bp.lg ? setVisible(false) : setVisible(true);
  }, [bp]);

  useEffect(() => {
    if (value === undefined) {
      setItems([]);
    }
  }, [value]);

  const filters = useMemo(() => {
    return schema.filter((s) => items.indexOf(s.id) === -1);
  }, [items, schema]);
  return (
    <div className="qbld">
      {props.children}
      {props.search && (
        <div className={'qbld-search-div'}>
          <Input.Search
            className="qbld-search"
            allowClear={true}
            value={props.value?.search}
            placeholder={props.searchPlaceholder}
            onChange={(event) =>
              props.onChange?.({
                ...props.value,
                search: event.target.value === '' ? undefined : event.target.value,
              })
            }
            onSearch={(value) => {
              const val = { ...props.value, search: value === '' ? undefined : value };
              props.onChange?.(val);
              props.onSearch?.(val);
            }}
          />
        </div>
      )}
      {!bp.lg ? (
        <Button
          style={{ marginTop: '20px' }}
          onClick={() => setVisible(!visible)}
          icon={<FilterOutlined />}
          size={'middle'}
        />
      ) : (
        ''
      )}
      {visible &&
        items
          .map((item) => schema.find((f) => f.id === item))
          .filter((f) => f !== undefined)
          .map((f) => f!)
          .map((item) => (
            <QueryBuilderItem
              key={item.id}
              editing={editing === item.id}
              setEditing={(e) => {
                if (e) {
                  setEditing(item.id);
                } else {
                  setEditing(undefined);
                  props.onSearch?.(props.value);
                }
              }}
              adapters={adapters}
              field={item}
              onChange={(value) => {
                const v = { ...(props.value ?? {}) };
                v[item.id] = value;
                props.onChange?.(v);
              }}
              value={(props.value ?? {})[item.id]}
              onClose={() => {
                setItems(items.filter((i) => i !== item.id));
                const v = { ...(props.value ?? {}) };
                delete v[item.id];
                props.onChange?.(v);
                props.onSearch?.(v);
              }}
            />
          ))}
      {visible && filters.length > 0 && (
        <Select value={sel} className="qbld-field-select" placeholder="Filter by" onChange={setSel}>
          {filters.map((s) => (
            <Select.Option key={s.id} value={s.id}>
              {s.name}
            </Select.Option>
          ))}
        </Select>
      )}
    </div>
  );
};
